import type { EmployeeLight } from '@/types/external/2work/api/response/EmployeeLight'
import type { EmployeeDynamic } from '@/types/external/2work/doctypes/generatorOutput/Setup/EmployeeDynamic'

type EmployeeReference = Pick<EmployeeDynamic, 'employee_name' | 'image' | 'name'>

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim() !== ''
}

const getEmployeeImageUrl = (
  image: null | string | undefined,
  showEmployeeImage: (path: string) => string,
): null | string => {
  if (!image) {
    return null
  }

  if (
    image.startsWith('/api/method/') ||
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('data:')
  ) {
    return image
  }

  return showEmployeeImage(image)
}

const normalizeEmployeeReference = (
  employee: unknown,
): { id?: string; image?: string; label?: string } => {
  const normalizedReference: { id?: string; image?: string; label?: string } = {}

  if (!employee) {
    return normalizedReference
  }

  if (typeof employee === 'string') {
    normalizedReference.id = employee
    normalizedReference.label = employee

    return normalizedReference
  }

  if (typeof employee !== 'object') {
    return normalizedReference
  }

  const employeeRecord: Record<string, unknown> = { ...employee }
  const id = [
    employeeRecord['name'],
    employeeRecord['employee'],
    employeeRecord['employee_id'],
    employeeRecord['value'],
  ].find(isNonEmptyString)
  const label = [
    employeeRecord['employee_name'],
    employeeRecord['full_name'],
    employeeRecord['label'],
    employeeRecord['optionLabel'],
    id,
  ].find(isNonEmptyString)
  const image = [employeeRecord['image'], employeeRecord['imageUrl']].find(isNonEmptyString)

  if (id) {
    normalizedReference.id = id
  }
  if (label) {
    normalizedReference.label = label
  }
  if (image) {
    normalizedReference.image = image
  }

  return normalizedReference
}

const resolveEmployeeReference = (
  employee: unknown,
  employeesById: Map<string, EmployeeLight>,
  employeesByLabel: Map<string, EmployeeLight>,
): EmployeeReference | undefined => {
  const normalizedReference = normalizeEmployeeReference(employee)
  let resolvedEmployeeReference: EmployeeReference | undefined = undefined

  if (normalizedReference.id || normalizedReference.label || normalizedReference.image) {
    const resolvedEmployee =
      (normalizedReference.id ? employeesById.get(normalizedReference.id) : undefined) ??
      (normalizedReference.label ? employeesByLabel.get(normalizedReference.label) : undefined)

    if (resolvedEmployee) {
      resolvedEmployeeReference = {
        employee_name: resolvedEmployee.employee_name,
        name: resolvedEmployee.name,
        ...(resolvedEmployee.image ? { image: resolvedEmployee.image } : {}),
      }
    } else {
      const fallbackName = normalizedReference.id ?? normalizedReference.label
      const fallbackEmployeeName = normalizedReference.label ?? normalizedReference.id

      if (fallbackName || fallbackEmployeeName) {
        resolvedEmployeeReference = {
          employee_name: fallbackEmployeeName ?? '',
          ...(normalizedReference.image ? { image: normalizedReference.image } : {}),
          name: fallbackName ?? '',
        }
      }
    }
  }

  return resolvedEmployeeReference
}

export {
  type EmployeeReference,
  getEmployeeImageUrl,
  normalizeEmployeeReference,
  resolveEmployeeReference,
}
